/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-floating-promises */
import { useAuthInfo } from "@propelauth/react";
import { useMutation } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import {
  Columns3,
  Download,
  Filter,
  Grid3X3,
  MoreHorizontal,
  Plus,
  Save,
  Search,
  Settings,
  SlidersHorizontal,
  List,
  LayoutGrid,
  Trash2,
  Pencil,
  Globe,
  Lock,
  Zap,
  ChevronDown,
} from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { queryClient, useCreateExport } from "../../../../hooks/QueryHooks";
import {
  createView,
  deleteViewInApi,
  updateViewInApi,
} from "../../../../services/view/viewEndpoints";
import ViewIconDisplay from "../../../../shared/components/ViewIconDisplay";
import { filterIncompletePredicates } from "../../../../shared/filter/utils/filterValidation";
import MultiSelectDropdown from "../../../../shared/ui/dropdown/MultiSelectDropdown";
import SortDropdown from "../../../../shared/ui/dropdown/SortDropdown";
import useSignalStore from "../../../../store/signal/signalStore";
import useViewStore from "../../../../store/view/viewStore";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import { toast } from "sonner";
import ViewIcon from "../../../../shared/ui/icons/IconPicker/ViewIcon";
import CreateView from "../../components/CreateView";
import SignalFilterPopover from "../../signals/signal-overview/components/SignalFilterPopover";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { Input } from "../../../../components/ui/input";
import { Switch } from "../../../../components/ui/switch";
import { Textarea } from "../../../../components/ui/textarea";
import { cn } from "../../../../lib/utils";

type Props = {
  currentView: any;
  traits?: any;
  views?: any[];
};

type SelectedViewIcon = {
  type: "emoji" | "built_in";
  value: string;
};

const getIconSelectionFromView = (view: any): SelectedViewIcon | undefined => {
  const iconValue = view?.icon_key || view?.view_icon;
  if (!iconValue) return undefined;
  const normalizedType = (view?.icon_type === "custom" || view?.icon_type === "emoji")
    ? "emoji"
    : "built_in";
  return {
    type: normalizedType,
    value: iconValue,
  };
};

const mutateViewsCache = (workspaceId: string | undefined, updateViews: (views: any[]) => any[]) => {
  if (!workspaceId) return;

  queryClient.setQueryData(["views", workspaceId], (oldData: any) => {
    if (!oldData) return oldData;

    if (Array.isArray(oldData)) {
      return updateViews(oldData);
    }

    if (Array.isArray(oldData?.data?.views)) {
      return {
        ...oldData,
        data: {
          ...oldData.data,
          views: updateViews(oldData.data.views),
        },
      };
    }

    return oldData;
  });
};

const AccountControls = memo(({
  currentView,
  traits,
  views = [],
}: Props) => {
  const [create, setCreate] = useState(false);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showColumns, setShowColumns] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const [settingsName, setSettingsName] = useState("");
  const [settingsDescription, setSettingsDescription] = useState("");
  const [settingsIsPublic, setSettingsIsPublic] = useState(false);
  const [settingsIcon, setSettingsIcon] = useState<SelectedViewIcon | undefined>(undefined);
  const [settingsTargetView, setSettingsTargetView] = useState<any>(null);
  const { updateView, setView } = useViewStore();
  const { setAllFilters, setName } = useSignalStore();
  const { user } = useAuthInfo();
  const currentWorkspace = useWorkspaceStore((state) => state.workspace);
  const navigate = useNavigate();

  const lastSavedRef = useRef<any>(currentView);

  useEffect(() => {
    if (currentView?.id && lastSavedRef.current?.id !== currentView.id) {
      lastSavedRef.current = currentView;
    }
    setSearchQuery("");
    setSettingsTargetView(currentView || null);
    setSettingsName(currentView?.view_name || "");
    setSettingsDescription(currentView?.view_description || "");
    setSettingsIsPublic(Boolean(currentView?.is_public));
    setSettingsIcon(getIconSelectionFromView(currentView));
  }, [currentView?.id]);

  const hasUnsavedChanges = useMemo(() => {
    if (!currentView) return false;
    return !isEqual(currentView, lastSavedRef.current);
  }, [currentView]);

  const createViewMutation = useMutation({
    mutationFn: createView,
  });

  const updateViewMutation = useMutation({
    mutationFn: updateViewInApi,
  });

  const deleteViewMutation = useMutation({
    mutationFn: deleteViewInApi,
  });

  const createExportMutation = useCreateExport();

  const mergedFieldMap = useMemo(
    () => ({ ...(traits?.obj || {}) }),
    [traits?.obj],
  );

  const selectedColumns = useMemo(
    () => currentView?.selected_columns || [],
    [currentView?.selected_columns],
  );

  const filteredTraits = useMemo(
    () => traits?.list?.filter((item: any) =>
      !item.exclude_display
      && item?.attribute_type !== "metric_traits"
      && item?.generation_type !== "computed_trait"
    ) || [],
    [traits?.list],
  );

  const filteredSuccessMetrics = useMemo(
    () => traits?.list?.filter((item: any) =>
      !item.exclude_display && item?.generation_type === "computed_trait"
    ) || [],
    [traits?.list],
  );

  const filteredMetricTraits = useMemo(
    () => traits?.list?.filter((item: any) => !item.exclude_display && item?.attribute_type === "metric_traits") || [],
    [traits?.list],
  );

  const sort = useMemo(() => {
    const selectedSort = mergedFieldMap?.[currentView?.sort_fields?.field];
    return {
      name: selectedSort?.display_name,
      type: selectedSort?.data_type,
      entityType: selectedSort?.attribute_type,
      value: selectedSort?.name,
    };
  }, [mergedFieldMap, currentView?.sort_fields?.field]);

  const sortDir = useMemo(
    () => ({
      order: currentView?.sort_fields?.order || "desc",
      type: currentView?.sort_fields?.type || "property",
    }),
    [currentView?.sort_fields?.order, currentView?.sort_fields?.type],
  );

  const entityViews = useMemo(
    () => views.filter((view: any) => view?.entity_relation === currentView?.entity_relation),
    [views, currentView?.entity_relation],
  );

  const filteredViews = useMemo(() => {
    if (!searchQuery.trim()) return entityViews;
    const query = searchQuery.toLowerCase();
    return entityViews.filter((view: any) => (view?.view_name || "").toLowerCase().includes(query));
  }, [entityViews, searchQuery]);

  const visibleColumnCount = useMemo(
    () => selectedColumns.filter((item: any) => !traits?.obj?.[item]?.exclude_display).length,
    [selectedColumns, traits?.obj],
  );

  const entityLabel = currentView?.entity_relation === "users"
    ? "contacts"
    : currentView?.entity_relation || "accounts";
  const defaultEntitySubtitle = `All ${entityLabel} under management`;
  const entitySubtitle = currentView?.view_description?.trim() || defaultEntitySubtitle;
  const activeFilterCount = currentView?.filters?.predicates?.length || 0;
  const displayMode = useMemo(
    () => (["kanban", "board", "cards"].includes(currentView?.display_mode) ? "kanban" : "list"),
    [currentView?.display_mode],
  );

  const sanitizeFilters = (view: any) => ({
    ...view?.filters,
    predicates: filterIncompletePredicates(view?.filters?.predicates || []).map((group: any) => ({
      ...group,
      predicates: filterIncompletePredicates(group?.predicates || []),
    })),
  });

  const applyUpdatedView = (updatedView: any, makeCurrent = true) => {
    if (!updatedView?.id) return;
    mutateViewsCache(
      currentWorkspace?.id,
      (existingViews) => existingViews.map((item: any) => (item.id === updatedView.id ? updatedView : item)),
    );
    if (makeCurrent || updatedView.id === currentView?.id) {
      lastSavedRef.current = updatedView;
      setView(updatedView);
    }
  };

  const openSettingsForView = (view: any) => {
    if (!view) return;
    setSettingsTargetView(view);
    setSettingsName(view?.view_name || "");
    setSettingsDescription(view?.view_description || "");
    setSettingsIsPublic(Boolean(view?.is_public));
    setSettingsIcon(getIconSelectionFromView(view));
    setShowSettingsModal(true);
  };

  const handleSettingsSave = () => {
    const targetView = settingsTargetView || currentView;
    if (!targetView?.id) return;

    if (targetView?.is_precreated) {
      toast.error("Default views cannot be edited.");
      return;
    }

    const trimmedName = settingsName.trim();
    if (!trimmedName) {
      toast.error("View name required", { description: "View name cannot be empty." });
      return;
    }

    if (trimmedName.length < 3) {
      toast.error("View name too short", { description: "View name must be at least 3 characters long." });
      return;
    }

    const iconData = settingsIcon
      ? {
        icon_type: settingsIcon.type === "emoji" ? "emoji" : settingsIcon.type,
        icon_key: settingsIcon.value,
      }
      : {
        icon_type: null,
        icon_key: null,
      };

    updateViewMutation.mutate({
      ...targetView,
      view_name: trimmedName,
      view_description: settingsDescription.trim() || null,
      is_public: settingsIsPublic,
      filters: sanitizeFilters(targetView),
      workspace_id: currentWorkspace?.id,
      ...iconData,
    }, {
      onSuccess: (data: any) => {
        applyUpdatedView(data?.data, targetView?.id === currentView?.id);
        setShowSettingsModal(false);
        toast.success("View settings updated successfully");
      },
      onError: (error: any) => {
        if (error?.response?.status !== 0) {
          toast.error("Unknown error.", { description: "An unknown error occurred while updating this view." });
        }
      },
    });
  };

  const handleVisibilityToggle = () => {
    if (currentView?.is_precreated) {
      toast.error("Default views visibility cannot be changed.");
      return;
    }

    const nextVisibility = !currentView?.is_public;
    updateViewMutation.mutate({
      ...currentView,
      is_public: nextVisibility,
      filters: sanitizeFilters(currentView),
      workspace_id: currentWorkspace?.id,
    }, {
      onSuccess: (data: any) => {
        applyUpdatedView(data?.data);
        toast.success(nextVisibility ? "View is now public" : "View is now private");
      },
      onError: (error: any) => {
        if (error?.response?.status !== 0) {
          toast.error("Unknown error.", { description: "Unable to update view visibility." });
        }
      },
    });
  };

  const handleNewView = (name: string, _: string, isPublic: string) => {
    createViewMutation.mutate({
      ...currentView,
      filters: sanitizeFilters(currentView),
      owner_id: user?.userId,
      workspace_id: currentWorkspace?.id,
      view_name: name,
      is_public: isPublic,
      is_precreated: false,
    }, {
      onSuccess: (data: any) => {
        mutateViewsCache(currentWorkspace?.id, (existingViews) => [...existingViews, data?.data]);
        toast.success("View created successfully");
        navigate(`/home/${currentWorkspace?.unique_identifier}/view/${data?.data?.id}`);
      },
      onError: (error: any) => {
        if (error?.response?.status !== 0) {
          toast.error("Unknown error.", { description: "An unknown error occurred while creating the new view. Please try again later or contact support." });
        }
      },
    });
  };

  const handleViewSave = (onSuccess?: () => void) => {
    updateViewMutation.mutate({
      ...currentView,
      filters: sanitizeFilters(currentView),
      owner_id: user?.userId,
      workspace_id: currentWorkspace?.id,
    }, {
      onSuccess: (data: any) => {
        applyUpdatedView(data?.data);
        toast.success("View updated successfully");
        onSuccess?.();
      },
      onError: (error: any) => {
        if (error?.response?.status !== 0) {
          toast.error("Unknown error.", { description: "An unknown error occurred while updating the view. Please try again later or contact support." });
        }
      },
    });
  };

  const handleNewSignal = () => {
    setName(currentView?.name || currentView?.view_name || "");
    setAllFilters(currentView?.filters);
    navigate(`/home/${currentWorkspace?.unique_identifier}/signals/new`);
  };

  const initiateExport = () => {
    if (!currentView?.id) {
      toast.error("Error", { description: "No view selected for export" });
      return;
    }

    createExportMutation.mutate({
      workspace_id: currentWorkspace?.id,
      view_id: currentView.id,
    }, {
      onSuccess: () => {
        toast.success("Export initiated", { description: "Your export is being processed. You can view it in the Exports page." });
      },
      onError: (error: any) => {
        toast.error("Export failed", { description: error?.response?.data?.message || "An error occurred while processing your export" });
      },
    });
  };

  const handleExport = () => {
    if (currentView?.is_precreated) {
      initiateExport();
      return;
    }

    if (hasUnsavedChanges) {
      setShowSavePrompt(true);
      return;
    }

    initiateExport();
  };

  const handleSaveAndExport = () => {
    handleViewSave(() => {
      setShowSavePrompt(false);
      initiateExport();
    });
  };

  const onSelectSort = (field: any, dir: any) => {
    updateView({
      sort_fields: {
        field: field?.value,
        order: dir?.order,
        type: dir?.type || "property",
      },
    });
  };

  const onSelectColumn = (item: any) => {
    updateView({
      selected_columns: selectedColumns.includes(item.name)
        ? selectedColumns.filter((name: any) => name !== item.name)
        : [...selectedColumns, item.name],
    });
  };

  const onViewSelect = (view: any) => {
    if (!view?.id || view.id === currentView?.id) return;
    navigate(`/home/${currentWorkspace?.unique_identifier}/view/${view.id}`);
  };

  const onDisplayModeChange = (mode: "list" | "kanban") => {
    if (displayMode === mode) return;
    updateView({ display_mode: mode });
  };

  const handleDeleteView = () => {
    if (!currentView?.id) return;

    if (currentView?.is_precreated) {
      toast.error("Default views cannot be deleted.");
      return;
    }

    deleteViewMutation.mutate({
      workspace_id: currentWorkspace?.id,
      id: currentView.id,
    }, {
      onSuccess: () => {
        mutateViewsCache(
          currentWorkspace?.id,
          (existingViews) => existingViews.filter((item: any) => item.id !== currentView.id),
        );

        toast.success("View deleted successfully");

        const remainingViews = views.filter((item: any) => item.id !== currentView.id);
        const sameEntityDefault = remainingViews.find(
          (item: any) => item?.entity_relation === currentView?.entity_relation && item?.is_precreated,
        );
        const sameEntityAny = remainingViews.find(
          (item: any) => item?.entity_relation === currentView?.entity_relation,
        );
        const fallbackView = sameEntityDefault || sameEntityAny || remainingViews[0];
        setShowDeletePrompt(false);

        if (fallbackView?.id) {
          navigate(`/home/${currentWorkspace?.unique_identifier}/view/${fallbackView.id}`);
          return;
        }
        navigate(`/home/${currentWorkspace?.unique_identifier}/headquarters`);
      },
      onError: (error: any) => {
        if (error?.response?.status !== 0) {
          toast.error("Unknown error.", { description: "An unknown error occurred while deleting this view." });
        }
      },
    });
  };

  return (
    <>
      <div className="border-b border-border bg-background">
        <div className="px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-muted-foreground">
              {(currentView?.icon_key || currentView?.view_icon) ? (
                <ViewIconDisplay
                  iconType={currentView?.icon_type || "built_in"}
                  iconKey={currentView?.icon_key || currentView?.view_icon}
                  className="h-4 w-4"
                  size={16}
                />
              ) : (
                <Grid3X3 className="h-4 w-4" />
              )}
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-foreground truncate">{currentView?.view_name || "View"}</h1>
              <p className="text-sm text-muted-foreground">{entitySubtitle}</p>
            </div>
          </div>

          <Button
            variant="default"
            size="sm"
            className="gap-2"
            onClick={() => setCreate(true)}
          >
            <Plus className="h-4 w-4" />
            New View
          </Button>
        </div>

        <div className="px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  {(currentView?.icon_key || currentView?.view_icon) ? (
                    <ViewIconDisplay
                      iconType={currentView?.icon_type || "built_in"}
                      iconKey={currentView?.icon_key || currentView?.view_icon}
                      className="h-4 w-4 text-primary"
                      size={16}
                    />
                  ) : (
                    <Grid3X3 className="h-4 w-4 text-primary" />
                  )}
                  {currentView?.view_name || "Select view"}
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[288px] p-0">
                <div className="p-2">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search views..."
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.currentTarget.value)}
                      onKeyDown={(event) => event.stopPropagation()}
                      className="h-8 pl-8"
                    />
                  </div>
                </div>
                <DropdownMenuSeparator />
                <div className="max-h-[260px] overflow-y-auto py-1">
                  {filteredViews.map((view: any) => (
                    <DropdownMenuItem
                      key={view.id}
                      onSelect={() => onViewSelect(view)}
                      className={view.id === currentView?.id ? "bg-accent-tint text-primary" : undefined}
                    >
                      <div className="flex w-full items-center gap-2">
                        {(view?.icon_key || view?.view_icon) ? (
                          <ViewIconDisplay
                            iconType={view?.icon_type || "built_in"}
                            iconKey={view?.icon_key || view?.view_icon}
                            className="h-4 w-4"
                            size={16}
                          />
                        ) : (
                          <Grid3X3 className="h-4 w-4" />
                        )}
                        <span className="flex-1 truncate">{view?.view_name}</span>
                        <button
                          type="button"
                          aria-label={`Open options for ${view?.view_name || "view"}`}
                          className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:bg-muted"
                          onPointerDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            openSettingsForView(view);
                          }}
                        >
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </DropdownMenuItem>
                  ))}
                  {!filteredViews.length && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">No views found</div>
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setCreate(true)} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create new view
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <SignalFilterPopover
              filters={currentView?.filters}
              entityRelation={currentView?.entity_relation || "accounts"}
              onApply={(newFilters) => {
                const updatedColumns = [...selectedColumns];
                let hasChanges = false;
                const viewEntity = currentView?.entity_relation || "accounts";

                newFilters?.predicates?.forEach((group: any) => {
                  group?.predicates?.forEach((predicate: any) => {
                    const predicateField = predicate?.field;
                    if (!predicateField || predicateField === "") return;

                    // Skip related object fields — they can't be shown as columns
                    const dotIdx = predicateField.indexOf(".");
                    if (dotIdx > 0) {
                      const fieldPrefix = predicateField.substring(0, dotIdx);
                      if (fieldPrefix !== viewEntity) return;
                    }

                    if (predicateField.startsWith(`${viewEntity}.`)
                      && !updatedColumns.includes(predicateField)) {
                      updatedColumns.push(predicateField);
                      hasChanges = true;
                    }

                    if (mergedFieldMap?.[predicateField] && !updatedColumns.includes(predicateField)) {
                      updatedColumns.push(predicateField);
                      hasChanges = true;
                    }
                  });
                });

                const payload: any = { filters: newFilters };
                if (hasChanges) {
                  payload.selected_columns = updatedColumns;
                }
                updateView(payload);
              }}
              target={(
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 !border-primary !text-primary hover:!bg-accent-tint"
                >
                  <Filter className="h-4 w-4" />
                  {`Filter${activeFilterCount ? ` (${activeFilterCount})` : ""}`}
                </Button>
              )}
            />

            <MultiSelectDropdown
              selected={[...selectedColumns]}
              setSelected={onSelectColumn}
              isOpen={showColumns}
              setIsOpen={setShowColumns}
              list={filteredTraits}
              successMetricsList={filteredSuccessMetrics}
              position="bottom-start"
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowColumns((state) => !state)}
                className="gap-2"
              >
                <Columns3 className="h-4 w-4" />
                {`Edit Columns (${visibleColumnCount})`}
              </Button>
            </MultiSelectDropdown>

            {sort?.name && (
              <SortDropdown
                selectedColumns={[...selectedColumns, "last_seen"]}
                selectSort={sort}
                selectSortDir={sortDir}
                isOpen={showSort}
                setIsOpen={setShowSort}
                list={[...filteredTraits, ...filteredMetricTraits]}
                onDone={onSelectSort}
              >
                <Button
                  size="sm"
                  variant="outline"
                >
                  {`Sort: ${mergedFieldMap?.[sort?.value]?.display_name || sort?.name}`}
                </Button>
              </SortDropdown>
            )}

          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-lg border border-border bg-muted p-0.5">
              <button
                type="button"
                onClick={() => onDisplayModeChange("list")}
                className={cn(
                  "flex items-center gap-1.5 rounded-[5px] px-3 py-1 text-sm font-medium transition-colors",
                  displayMode === "list"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <List className="h-3.5 w-3.5" />
                List
              </button>
              <button
                type="button"
                onClick={() => onDisplayModeChange("kanban")}
                className={cn(
                  "flex items-center gap-1.5 rounded-[5px] px-3 py-1 text-sm font-medium transition-colors",
                  displayMode === "kanban"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                Kanban
              </button>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="!h-7 !w-7 !px-0" aria-label="View settings">
                  <Settings className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[220px]">
                <DropdownMenuItem
                  disabled={currentView?.is_precreated}
                  onSelect={() => {
                    if (currentView?.is_precreated) return;
                    openSettingsForView(currentView);
                  }}
                  className="gap-2"
                >
                  <Pencil className="h-4 w-4" />
                  Edit view details
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={currentView?.is_precreated}
                  onSelect={handleVisibilityToggle}
                  className="gap-2"
                >
                  {currentView?.is_public ? <Lock className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
                  {currentView?.is_public ? "Make private" : "Make public"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={currentView?.is_precreated ? () => setCreate(true) : () => handleViewSave()}
                  className="gap-2"
                >
                  <Save className="h-4 w-4" />
                  {currentView?.is_precreated ? "Create View" : "Save View"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handleExport}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handleNewSignal}
                  className="gap-2"
                >
                  <Zap className="h-4 w-4" />
                  Create Signal
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  disabled={currentView?.is_precreated}
                  onSelect={() => setShowDeletePrompt(true)}
                  className="gap-2 text-destructive data-[highlighted]:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete View
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <Dialog open={showSavePrompt} onOpenChange={setShowSavePrompt}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Save changes before export</DialogTitle>
            <DialogDescription>
              You have unsaved changes to this view. Would you like to save the changes before exporting?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowSavePrompt(false);
                initiateExport();
              }}
            >
              Export without saving
            </Button>
            <Button
              variant="default"
              onClick={handleSaveAndExport}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Save and Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>View settings</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-foreground">Icon</span>
              <ViewIcon
                selectedIcon={settingsIcon}
                onSelect={(value) => setSettingsIcon(value)}
                size={20}
                className="cursor-pointer"
              />
            </div>

            <div className="space-y-1.5">
              <p className="text-sm font-medium text-foreground">
                View name
              </p>
              <Input
                id="view-name-input"
                value={settingsName}
                onChange={(event) => setSettingsName(event.currentTarget.value)}
                placeholder="Enter view name"
              />
            </div>

            <div className="space-y-1.5">
              <p className="text-sm font-medium text-foreground">
                Description
              </p>
              <Textarea
                id="view-description-input"
                value={settingsDescription}
                onChange={(event) => setSettingsDescription(event.currentTarget.value)}
                placeholder="Add a short description"
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
              <span className="text-sm text-foreground">
                {settingsIsPublic ? "This view is public" : "This view is private"}
              </span>
              <Switch
                checked={settingsIsPublic}
                onCheckedChange={(checked) => setSettingsIsPublic(Boolean(checked))}
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowSettingsModal(false)}>Cancel</Button>
            <Button variant="default" onClick={handleSettingsSave}>Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeletePrompt} onOpenChange={setShowDeletePrompt}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete view</DialogTitle>
            <DialogDescription>
              This action cannot be undone. Are you sure you want to delete this view?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDeletePrompt(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDeleteView}
            >
              <Trash2 className="h-4 w-4" />
              Delete View
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {create && (
        <CreateView
          open={create}
          onClick={handleNewView}
          setOpen={setCreate}
          defaultType={currentView?.entity_relation || "accounts"}
          disableType
        />
      )}
    </>
  );
});

AccountControls.displayName = "AccountControls";

export default AccountControls;
